import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';

// ---------------------------------------------------------------------------
// Site Progress Tracker — portfolio rollup of the hub pipeline cycle.
//
// One row per registry site, one column per hub stage (uGridPREDICT excluded
// by product decision). A stage is "done" from the manifest pipeline_state —
// the same truth the dependency wheel renders — and the expandable detail
// lists the stage's input/output datasets with their updated_at stamps so a
// reviewer can see exactly WHAT is missing, not just that something is.
// ---------------------------------------------------------------------------

type PipelineState = Record<string, boolean>;

interface SiteStatus {
  key: string;
  name: string;
  code: string;
  country: string;
  state: 'draft' | 'published';
  pipeline_state: PipelineState;
  datasets: Record<string, { updated_at: string | null; updated_by?: string | null }>;
  last_update: string | null;
}

interface StageDef {
  id: string;
  label: string;
  flag: string;
  inputs: string[];
  outputs: string[];
  route: (key: string) => string;
}

const STAGES: StageDef[] = [
  {
    id: 'connections', label: 'Connections', flag: 'connections_loaded',
    inputs: [], outputs: ['connections'],
    route: (k) => `/planner?projectId=${encodeURIComponent(k)}`,
  },
  {
    id: 'ugridnet', label: 'Network', flag: 'network_designed',
    inputs: ['connections', 'exclusion_zones'], outputs: ['network_layout'],
    route: (k) => `/design-grid?project=${encodeURIComponent(k)}`,
  },
  {
    id: 'd8760', label: '8760', flag: 'demand_profiled',
    inputs: ['connections'], outputs: ['demand_8760'],
    route: (k) => `/8760?projectId=${encodeURIComponent(k)}`,
  },
  {
    id: 'ugrid', label: 'Sizing', flag: 'sizing_complete',
    inputs: ['demand_8760', 'tmy'], outputs: ['sizing'],
    route: (k) => `/design-plant?projectId=${encodeURIComponent(k)}`,
  },
  {
    id: 'ugridplan', label: 'Construction', flag: 'construction_started',
    inputs: ['network_layout', 'sizing'], outputs: ['construction'],
    route: (k) => `/planner?projectId=${encodeURIComponent(k)}`,
  },
];

const DATASET_LABELS: Record<string, string> = {
  connections: 'Connections',
  exclusion_zones: 'Exclusion zones',
  tmy: 'TMY weather',
  demand_8760: '8760 demand profile',
  pv_8760: 'PV 8760 profile',
  sizing: 'Sizing result',
  network_layout: 'Network layout',
  construction: 'Construction data',
  gensite_perimeter: 'Gensite perimeter',
  prediction: 'Prediction',
  tenure_trend_overrides: 'Tenure trend overrides',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 10) : d.toISOString().slice(0, 10);
}

function stageDone(site: SiteStatus, stage: StageDef): boolean {
  return !!site.pipeline_state?.[stage.flag];
}

function doneCount(site: SiteStatus): number {
  return STAGES.filter((s) => stageDone(site, s)).length;
}

export default function SiteProgressTracker() {
  const [sites, setSites] = useState<SiteStatus[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiService.getProjectsPipelineStatus();
        if (!cancelled) setSites(res.projects || []);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Could not load site progress');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    if (!sites) return [];
    const q = query.trim().toLowerCase();
    return sites
      .filter((s) => {
        if (hideEmpty && doneCount(s) === 0) return false;
        if (!q) return true;
        return (
          s.name.toLowerCase().includes(q) ||
          s.key.toLowerCase().includes(q) ||
          (s.code || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const d = doneCount(b) - doneCount(a);
        if (d !== 0) return d;
        return (b.last_update || '').localeCompare(a.last_update || '');
      });
  }, [sites, query, hideEmpty]);

  const summary = useMemo(() => {
    if (!sites) return null;
    const started = sites.filter((s) => doneCount(s) > 0);
    const complete = sites.filter((s) => doneCount(s) === STAGES.length);
    return { total: sites.length, started: started.length, complete: complete.length };
  }, [sites]);

  return (
    <div className="max-w-7xl mx-auto px-6 pb-8">
      <div className="bg-white rounded-2xl shadow-sm border p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
          <h2 className="text-lg font-bold text-gray-900">Site progress tracker</h2>
          {summary && (
            <p className="text-xs text-gray-500">
              {summary.started} of {summary.total} sites started &middot; {summary.complete} fully through the cycle
            </p>
          )}
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Done / missing for each stage of the design cycle, per site (from each project&rsquo;s manifest — the same
          state the wheel above shows). Click a stage cell to open that tool for the site; expand a row for the
          input/output detail.
        </p>

        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by site name or code…"
            className="border rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideEmpty}
              onChange={(e) => setHideEmpty(e.target.checked)}
              className="rounded"
            />
            Hide sites with nothing started
          </label>
          {sites && (
            <span className="text-xs text-gray-400 ml-auto">
              showing {visible.length} of {sites.length}
            </span>
          )}
        </div>

        {error && <p className="text-sm text-amber-700">{error}</p>}
        {!sites && !error && <p className="text-sm text-gray-400">Loading site progress…</p>}

        {sites && visible.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b">
                  <th className="py-2 pr-3 font-semibold">Site</th>
                  {STAGES.map((s) => (
                    <th key={s.id} className="py-2 px-2 font-semibold text-center whitespace-nowrap">{s.label}</th>
                  ))}
                  <th className="py-2 px-3 font-semibold text-center">Cycle</th>
                  <th className="py-2 pl-3 font-semibold whitespace-nowrap">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((site) => {
                  const done = doneCount(site);
                  const pct = Math.round((done / STAGES.length) * 100);
                  const isOpen = expanded === site.key;
                  return (
                    <React.Fragment key={site.key}>
                      <tr
                        className="border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpanded(isOpen ? null : site.key)}
                      >
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <span className={`inline-block text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}>&#9654;</span>
                            <div>
                              <span className="font-medium text-gray-900">{site.name}</span>
                              {site.code && (
                                <span className="ml-2 text-[10px] font-mono text-gray-400">{site.code}</span>
                              )}
                              {site.state === 'draft' && (
                                <span className="ml-2 px-1.5 py-0.5 rounded uppercase tracking-wider text-[9px] bg-violet-100 text-violet-700">draft</span>
                              )}
                            </div>
                          </div>
                        </td>
                        {STAGES.map((stage) => {
                          const ok = stageDone(site, stage);
                          return (
                            <td key={stage.id} className="py-2 px-2 text-center">
                              <Link
                                to={stage.route(site.key)}
                                onClick={(e) => e.stopPropagation()}
                                title={`${stage.label}: ${ok ? 'done' : 'missing'} — open tool`}
                                className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors ${
                                  ok
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                }`}
                              >
                                {ok ? '✓' : '—'}
                              </Link>
                            </td>
                          );
                        })}
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 rounded-full bg-gray-100 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">{done}/{STAGES.length}</span>
                          </div>
                        </td>
                        <td className="py-2 pl-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(site.last_update)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b last:border-b-0 bg-gray-50/60">
                          <td colSpan={STAGES.length + 3} className="py-3 px-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                              {STAGES.map((stage) => {
                                const ok = stageDone(site, stage);
                                const slots = [
                                  ...stage.inputs.map((d) => ({ key: d, kind: 'in' as const })),
                                  ...stage.outputs.map((d) => ({ key: d, kind: 'out' as const })),
                                ];
                                return (
                                  <div key={stage.id} className="border rounded-lg p-3 bg-white">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-bold text-gray-800">{stage.label}</span>
                                      <span className={`text-[10px] font-semibold ${ok ? 'text-green-600' : 'text-gray-400'}`}>
                                        {ok ? 'done' : 'missing'}
                                      </span>
                                    </div>
                                    <ul className="space-y-1">
                                      {slots.map(({ key, kind }) => {
                                        const entry = site.datasets?.[key];
                                        const present = !!entry?.updated_at;
                                        return (
                                          <li key={`${kind}-${key}`} className="flex items-center justify-between text-[11px]">
                                            <span className="text-gray-600">
                                              <span className="text-gray-400 mr-1">{kind === 'in' ? 'in:' : 'out:'}</span>
                                              {DATASET_LABELS[key] || key}
                                            </span>
                                            <span className={present ? 'text-green-600 font-medium' : 'text-gray-400'}>
                                              {present ? `✓ ${fmtDate(entry?.updated_at)}` : '✗'}
                                            </span>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                    <Link
                                      to={stage.route(site.key)}
                                      className="inline-block mt-2 text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                      Open {stage.label} &rarr;
                                    </Link>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {sites && visible.length === 0 && !error && (
          <p className="text-sm text-gray-400">No sites match the current filter.</p>
        )}
      </div>
    </div>
  );
}
